import { useRestoreSlideMutation } from "@api/slides-library";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { cn } from "@lib/cn";
import { IconDots } from "@tabler/icons-react";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { useCallback } from "react";
import { toast } from "react-toastify";
import { handleAsyncOperation } from "@/shared";
import type { TSavedSlide } from "@/shared/types";
import { SlidePermanentDeleteDialog } from "../dialogs/slide-permanent-delete-dialog";
import { DeleteRestoreMenu } from "./delete-restore-menu";

export const ArchivedSlideActions = ({
  slide,
  isOpen,
  onOpenChange,
}: {
  slide: TSavedSlide;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const dispatch = useAppDispatch();
  const [restoreSlide] = useRestoreSlideMutation();

  const handleRestore = useCallback(async () => {
    onOpenChange(false);
    await handleAsyncOperation({
      operation: async () => {
        await restoreSlide({ id: slide._id }).unwrap();
        toast.success(`“${slide.title}” restored`);
      },
      onError: () => toast.error("Failed to restore slide"),
    });
  }, [restoreSlide, slide._id, slide.title, onOpenChange]);

  const handlePermanentDelete = useCallback(() => {
    onOpenChange(false);
    dispatch(
      openDialog({
        id: SlidePermanentDeleteDialog.id,
        content: SlidePermanentDeleteDialog,
        props: { slide },
      }),
    );
  }, [dispatch, slide, onOpenChange]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Open actions for archived slide: ${slide.title}`}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          className={cn(
            "tw-invisible tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border-none tw-bg-transparent tw-p-0 tw-opacity-0 tw-outline-none tw-transition-[opacity,background-color] hover:tw-bg-blue-100 group-hover/slide:tw-visible group-hover/slide:tw-opacity-100 group-focus-within/slide:tw-visible group-focus-within/slide:tw-opacity-100 focus:tw-visible focus:tw-opacity-100",
            isOpen && "!tw-visible !tw-bg-blue-100 !tw-opacity-100",
          )}
        >
          <IconDots size={14} stroke={1} className="tw-text-blue-800" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DeleteRestoreMenu onRestore={handleRestore} onDelete={handlePermanentDelete} />
    </DropdownMenu>
  );
};
